/**
 * Dark Automotive Homepage Motion & Interactive Engine
 * The UTE Shop — Apple-Style Hero Frame Scrubber + 3-Podium Studio Showcase Scrubber
 */

(function () {
  'use strict';

  function initHomepageEngine() {
    initHeroAutoPlayer();
    initStudioPodiumHorizontalShowcase();
    initScrollReveals();
    initHeaderScrollState();
    initProductGallerySwitcher();
  }

  /**
   * 1. Auto-Playing Canvas Frame Sequence Player & Slide Cycler (Hero)
   * Plays continuously at ~24-30 fps without scroll-jacking or parallax delays.
   */
  function initHeroAutoPlayer() {
    const pinWrap = document.querySelector('.hero-cinematic-pin-wrap');
    const canvas = document.getElementById('heroSequenceCanvas');
    if (!pinWrap || !canvas) return;

    const ctx = canvas.getContext('2d');
    const frameUrls = window.__HERO_FRAMES__ || [];
    if (!frameUrls.length) return;

    const textGroups = document.querySelectorAll('.hero-text-group');
    const pips = document.querySelectorAll('.hero-pip-pill');

    const totalFrames = frameUrls.length;
    const images = new Array(totalFrames);
    let currentFrame = 0;
    let activeGroupIndex = 0;
    let lastRenderedFrame = 0;

    // Resize Canvas to device pixel ratio & redraw
    function resizeCanvas() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      renderFrame(currentFrame);
    }

    function renderFrame(index) {
      let img = images[index];
      // If requested frame isn't loaded yet, fall back to last successfully loaded frame
      if (!img || !img.complete || !img.naturalWidth) {
        img = images[lastRenderedFrame];
      }
      if (!img || !img.complete || !img.naturalWidth) return;

      lastRenderedFrame = index;
      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;

      // Cover scaling math
      const scale = Math.max(cw / iw, ch / ih);
      const nw = iw * scale;
      const nh = ih * scale;
      const nx = (cw - nw) / 2;
      const ny = (ch - nh) / 2;

      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, nx, ny, nw, nh);
    }

    // Preload Frame 0 first for instant display
    const firstImg = new Image();
    firstImg.src = frameUrls[0];
    images[0] = firstImg;
    firstImg.onload = function () {
      resizeCanvas();
    };

    // Preload remaining frames progressively in background idle batches to prevent network congestion
    function loadFramesBatch(startIndex, batchSize) {
      if (startIndex >= totalFrames) return;
      const endIndex = Math.min(totalFrames, startIndex + batchSize);
      for (let i = startIndex; i < endIndex; i++) {
        if (!images[i]) {
          const img = new Image();
          img.src = frameUrls[i];
          images[i] = img;
        }
      }
      if (endIndex < totalFrames) {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => loadFramesBatch(endIndex, batchSize), { timeout: 800 });
        } else {
          setTimeout(() => loadFramesBatch(endIndex, batchSize), 35);
        }
      }
    }

    // Start background frame loading
    if (document.readyState === 'complete') {
      loadFramesBatch(1, 8);
    } else {
      window.addEventListener('load', function() {
        loadFramesBatch(1, 8);
      });
    }

    window.addEventListener('resize', resizeCanvas, { passive: true });

    function switchTextGroup(targetIndex) {
      if (activeGroupIndex === targetIndex && textGroups[targetIndex] && textGroups[targetIndex].classList.contains('is-active')) return;
      activeGroupIndex = targetIndex;

      textGroups.forEach(function (grp, idx) {
        if (idx === targetIndex) {
          grp.classList.add('is-active');
        } else {
          grp.classList.remove('is-active');
        }
      });

      pips.forEach(function (pip, idx) {
        if (idx === targetIndex) {
          pip.classList.add('is-active');
        } else {
          pip.classList.remove('is-active');
        }
      });
    }

    // Slide Cycling Timer (Changes text slide every 4.5s)
    let slideTimer = null;
    function autoAdvanceSlide() {
      if (textGroups.length === 0) return;
      const nextIndex = (activeGroupIndex + 1) % textGroups.length;
      switchTextGroup(nextIndex);
    }

    function startSlideTimer() {
      stopSlideTimer();
      slideTimer = setInterval(autoAdvanceSlide, 4500);
    }

    function stopSlideTimer() {
      if (slideTimer) {
        clearInterval(slideTimer);
        slideTimer = null;
      }
    }

    // Interactive Pip Click Support
    pips.forEach(function (pip) {
      pip.addEventListener('click', function () {
        const pipIndex = parseInt(this.getAttribute('data-pip'), 10);
        if (!isNaN(pipIndex)) {
          switchTextGroup(pipIndex);
        }
      });
    });

    // Auto-Play Frame Loop: Smooth Ping-Pong Video Loop with Easing
    // Spans out at the end (frame 119), loops back to front, slowing down as it reaches the front view!
    let animId = null;
    let isVisible = true;
    let animTime = 0;
    let lastTimestamp = null;
    const cycleDuration = 5200; // 5.2 seconds per direction

    function frameLoop(timestamp) {
      if (!isVisible) return;
      animId = requestAnimationFrame(frameLoop);

      if (!lastTimestamp) lastTimestamp = timestamp;
      const delta = Math.min(timestamp - lastTimestamp, 100);
      lastTimestamp = timestamp;
      animTime += delta;

      const totalCycle = cycleDuration * 2;
      const cycleProgress = (animTime % totalCycle) / totalCycle; // 0 to 1

      let easedProgress = 0;
      if (cycleProgress < 0.5) {
        // Forward direction: Front -> Spanning out to end
        const sub = cycleProgress / 0.5;
        // Cosine easing: starts smooth, accelerates in middle, spans out gently at the end
        easedProgress = 0.5 * (1 - Math.cos(Math.PI * sub));
      } else {
        // Return direction: Spanning back from end -> Front (progressively slowing down)
        const sub = (cycleProgress - 0.5) / 0.5;
        // Cosine easing: gently starts, moves, and decelerates smoothly as it reaches the front!
        easedProgress = 0.5 * (1 + Math.cos(Math.PI * sub));
      }

      const targetFrame = Math.max(0, Math.min(totalFrames - 1, Math.round(easedProgress * (totalFrames - 1))));
      if (targetFrame !== currentFrame) {
        currentFrame = targetFrame;
        renderFrame(currentFrame);
      }

      // Synchronize text slides smoothly with frame position
      if (easedProgress < 0.35) {
        switchTextGroup(0); // Front view: Sunshine Coast Specialists / Range
      } else if (easedProgress < 0.70) {
        switchTextGroup(1); // Mid view: Storage & Platform Racks
      } else {
        switchTextGroup(2); // End span view: Protection & Bull Bars
      }
    }

    // IntersectionObserver to pause playback when out of view (saves battery and GPU)
    if ('IntersectionObserver' in window) {
      const heroObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            isVisible = true;
            lastTimestamp = null;
            if (!animId) animId = requestAnimationFrame(frameLoop);
          } else {
            isVisible = false;
            if (animId) {
              cancelAnimationFrame(animId);
              animId = null;
            }
          }
        });
      }, { threshold: 0.05 });
      heroObserver.observe(pinWrap);
    } else {
      animId = requestAnimationFrame(frameLoop);
    }

    // Initial text state
    switchTextGroup(0);
  }

  /**
   * 2. 3-Podium Studio Horizontal Showcase (Smooth Interactive Drag, Swipe & Arrow Carousel)
   * Users can freely scroll past the section vertically without scroll-jacking.
   */
  function initStudioPodiumHorizontalShowcase() {
    const pinWrap = document.getElementById('promoHorizontalPin');
    const track = document.getElementById('promoTrack');
    const viewport = document.getElementById('promoTrackWrap');
    const progressBar = document.getElementById('promoProgressBar');
    const counters = document.querySelectorAll('.promoCounter');
    const prevBtns = document.querySelectorAll('.promoPrevBtn');
    const nextBtns = document.querySelectorAll('.promoNextBtn');
    const podiumCards = document.querySelectorAll('.promo-podium-card');

    if (!pinWrap || !track || !podiumCards.length) return;

    let currentIndex = 0;
    const totalCards = podiumCards.length;
    let currentX = 0;
    let targetX = 0;
    let isDragging = false;
    let startDragX = 0;
    let dragStartX = 0;
    let isAnimating = false;
    let cardCenters = [];

    function computeCardCenters() {
  const containerLeft = track.parentElement.getBoundingClientRect().left;
  const leftPadding = 80;
  cardCenters = Array.from(podiumCards).map(card => {
    return -(card.offsetLeft - containerLeft - leftPadding);
  });
}



    function updateCard3DTransforms(trackX) {
      const isMobile = window.innerWidth <= 768;
      const cardWidth = (podiumCards[0] && podiumCards[0].offsetWidth) ? podiumCards[0].offsetWidth : (isMobile ? 300 : 450);
      const spacingUnit = cardWidth * (isMobile ? 1.15 : 1.3);

      podiumCards.forEach(function (card, idx) {
        const targetCenterX = (cardCenters[idx] !== undefined) ? cardCenters[idx] : 0;
        const deltaX = trackX - targetCenterX;
        const normalizedOffset = deltaX / spacingUnit;
        const clampedOffset = Math.max(-2, Math.min(2, normalizedOffset));
        const absOffset = Math.abs(clampedOffset);

        // Center factor: 1.0 at center, 0.0 at side pedestals
        const centerFactor = Math.max(0, 1 - absOffset * 1.25);
        const stage = card.querySelector('.promo-podium-card__stage');
        const img = card.querySelector('.promo-podium-card__img');

        if (isMobile) {
          const translateY = (1 - centerFactor) * -4;
          card.style.transform = 'translate3d(0, ' + translateY + 'px, 0)';
          card.style.opacity = '1';

          if (stage) {
            const stageScale = 0.95 + (centerFactor * 0.08);
            stage.style.transform = 'scale(' + stageScale + ')';
            stage.style.borderColor = centerFactor > 0.6 ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.16)';
          }
        } else {
          const translateY = (1 - centerFactor) * -18;
          card.style.transform = 'translate3d(0, ' + translateY + 'px, 0)';
          card.style.opacity = '1';

          if (stage) {
            const stageScale = 0.92 + (centerFactor * 0.18);
            stage.style.transform = 'scale(' + stageScale + ')';
            stage.style.borderColor = centerFactor > 0.6 ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.16)';
            stage.style.boxShadow = centerFactor > 0.6 
              ? '0 25px 50px rgba(0, 0, 0, 0.9), 0 0 30px rgba(255, 255, 255, 0.15)' 
              : '0 15px 35px rgba(0, 0, 0, 0.7)';
          }
        }

        if (absOffset < 0.35) {
          card.classList.add('is-center');
        } else {
          card.classList.remove('is-center');
        }

        if (img) {
          const imgScale = isMobile ? 1.0 : (1.0 + (centerFactor * 0.08));
          img.style.transform = 'scale(' + imgScale + ')';
        }
      });

      // Update all counter instances
      const displayIndex = currentIndex + 1;
      const counterText = (displayIndex < 10 ? '0' + displayIndex : displayIndex) + ' / ' + (totalCards < 10 ? '0' + totalCards : totalCards);
      counters.forEach(c => {
        c.textContent = counterText;
      });

      if (progressBar) {
        const pct = totalCards > 1 ? (currentIndex / (totalCards - 1)) * 85 + 15 : 100;
        progressBar.style.width = Math.min(100, Math.max(15, pct)) + '%';
      }
    }

    function renderLoop() {
      const diff = targetX - currentX;
      if (Math.abs(diff) > 0.4) {
        currentX += diff * 0.18;
        track.style.transform = 'translate3d(' + currentX + 'px, 0, 0)';
        updateCard3DTransforms(currentX);
        requestAnimationFrame(renderLoop);
        isAnimating = true;
      } else {
        currentX = targetX;
        track.style.transform = 'translate3d(' + currentX + 'px, 0, 0)';
        updateCard3DTransforms(currentX);
        isAnimating = false;
      }
    }

    function goToCard(index) {
      currentIndex = Math.max(0, Math.min(totalCards - 1, index));
      computeCardCenters();
      if (cardCenters[currentIndex] !== undefined) {
        targetX = cardCenters[currentIndex];
        if (!isAnimating) {
          requestAnimationFrame(renderLoop);
        }
      }
    }

    function initPosition() {
      computeCardCenters();
      if (cardCenters.length > 0) {
        currentX = cardCenters[currentIndex];
        targetX = currentX;
        track.style.transform = 'translate3d(' + currentX + 'px, 0, 0)';
        updateCard3DTransforms(currentX);
      }
    }

    // Prev / Next button handlers
    prevBtns.forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        goToCard(currentIndex - 1);
      });
    });

    nextBtns.forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        goToCard(currentIndex + 1);
      });
    });

    // Mouse & Touch Dragging
    const dragArea = viewport || track;

    function onDragStart(clientX) {
      isDragging = true;
      startDragX = clientX;
      dragStartX = currentX;
      computeCardCenters();
      if (viewport) viewport.classList.add('is-dragging');
    }

    function onDragMove(clientX) {
      if (!isDragging) return;
      const delta = clientX - startDragX;
      targetX = dragStartX + delta;
      currentX = targetX;
      track.style.transform = 'translate3d(' + currentX + 'px, 0, 0)';
      updateCard3DTransforms(currentX);
    }

    function onDragEnd() {
      if (!isDragging) return;
      isDragging = false;
      if (viewport) viewport.classList.remove('is-dragging');

      // Find closest card to current targetX
      let closestIdx = 0;
      let minDistance = Infinity;
      cardCenters.forEach((centerOffset, idx) => {
        const dist = Math.abs(centerOffset - targetX);
        if (dist < minDistance) {
          minDistance = dist;
          closestIdx = idx;
        }
      });
      goToCard(closestIdx);
    }

    dragArea.addEventListener('mousedown', function (e) {
      if (e.target.closest('.promo-card-btn')) return;
      onDragStart(e.clientX);
    });

    window.addEventListener('mousemove', function (e) {
      onDragMove(e.clientX);
    });

    window.addEventListener('mouseup', onDragEnd);

    dragArea.addEventListener('touchstart', function (e) {
      if (e.touches.length === 1) {
        onDragStart(e.touches[0].clientX);
      }
    }, { passive: true });

    dragArea.addEventListener('touchmove', function (e) {
      if (e.touches.length === 1 && isDragging) {
        onDragMove(e.touches[0].clientX);
      }
    }, { passive: true });

    dragArea.addEventListener('touchend', onDragEnd);

    // Clicking a visible side card smoothly focuses it
    podiumCards.forEach(function (card, idx) {
      card.addEventListener('click', function (e) {
        if (idx !== currentIndex) {
          e.preventDefault();
          goToCard(idx);
        }
      });
    });

    window.addEventListener('resize', function () {
      goToCard(currentIndex);
    }, { passive: true });

    // Initial setup
    initPosition();
    setTimeout(initPosition, 100);
  }

  /**
   * 3. Intersection Observer Scroll Reveal Engine (for lower page sections)
   */
  function initScrollReveals() {
    const revealElements = document.querySelectorAll('[data-reveal]');
    if (!revealElements.length) return;

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      }
    );

    revealElements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /**
   * 4. Smart Header Hide on Scroll Down & Reveal on Scroll Up
   * Stays pinned/visible while the homepage hero animation is playing.
   */
  function initHeaderScrollState() {
    const headerSection = document.querySelector('.section-header');
    const headerWrapper = document.querySelector('.header-wrapper') || document.querySelector('sticky-header');
    if (!headerSection && !headerWrapper) return;

    let lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    let ticking = false;

    function getHeroEndThreshold() {
      const pinWrap = document.querySelector('.hero-cinematic-pin-wrap') || document.querySelector('.hero-cinematic-stage');
      if (pinWrap) {
        const rect = pinWrap.getBoundingClientRect();
        return Math.max(300, (window.scrollY || 0) + rect.bottom - window.innerHeight);
      }
      return 300;
    }

    function updateHeader() {
      const currentScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const scrollDiff = currentScrollY - lastScrollY;
      const heroThreshold = getHeroEndThreshold();

      // 1. Scrolled past top threshold -> elevated dark glass
      if (currentScrollY > 30) {
        if (headerWrapper) headerWrapper.classList.add('scrolled-past-header');
        if (headerSection) headerSection.classList.add('scrolled-past-header');
      } else {
        if (headerWrapper) headerWrapper.classList.remove('scrolled-past-header');
        if (headerSection) headerSection.classList.remove('scrolled-past-header');
      }

      // 2. Smart hide on scroll-down, reveal on scroll-up
      // At the top of page or within hero sequence -> ALWAYS visible
      if (currentScrollY <= heroThreshold || currentScrollY <= 60) {
        if (headerSection) headerSection.classList.remove('header-hidden', 'shopify-section-header-hidden');
        if (headerWrapper) headerWrapper.classList.remove('header-hidden', 'shopify-section-header-hidden');
      } else {
        if (scrollDiff > 6) {
          // Scrolling down -> hide header smoothly
          if (headerSection) headerSection.classList.add('header-hidden');
          if (headerWrapper) headerWrapper.classList.add('header-hidden');
        } else if (scrollDiff < -4) {
          // Scrolling up -> reveal header smoothly
          if (headerSection) headerSection.classList.remove('header-hidden', 'shopify-section-header-hidden');
          if (headerWrapper) headerWrapper.classList.remove('header-hidden', 'shopify-section-header-hidden');
        }
      }

      lastScrollY = currentScrollY;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );

    // Initial check on load
    updateHeader();
  }

  /**
   * 5. Interactive Single-Image Product Gallery Switcher
   */
  function initProductGallerySwitcher() {
    const gallery = document.querySelector('media-gallery') || document.querySelector('.product__media-wrapper');
    if (!gallery) return;

    const thumbnailButtons = gallery.querySelectorAll('.thumbnail-list__item button.thumbnail');
    const mediaItems = gallery.querySelectorAll('.product__media-list .product__media-item');
    if (!thumbnailButtons.length || !mediaItems.length) return;

    thumbnailButtons.forEach(function (btn, index) {
      function activateSlide(e) {
        if (e && e.type === 'click') e.preventDefault();

        // 1. Update active thumbnail aria-current
        thumbnailButtons.forEach(function (b) {
          b.removeAttribute('aria-current');
        });
        btn.setAttribute('aria-current', 'true');

        // 2. Resolve target media item ID or position
        const itemWrap = btn.closest('.thumbnail-list__item');
        const targetId = itemWrap ? itemWrap.getAttribute('data-target') : null;
        let matched = false;

        mediaItems.forEach(function (item, itemIndex) {
          const itemId = item.getAttribute('data-media-id') || item.id;
          if (targetId && itemId && (itemId === targetId || itemId.includes(targetId) || targetId.includes(itemId))) {
            item.classList.add('is-active');
            matched = true;
          } else if (!targetId && itemIndex === index) {
            item.classList.add('is-active');
            matched = true;
          } else {
            item.classList.remove('is-active');
          }
        });

        // Fallback: activate by index if ID didn't match
        if (!matched && mediaItems[index]) {
          mediaItems.forEach(function (item) {
            item.classList.remove('is-active');
          });
          mediaItems[index].classList.add('is-active');
        }
      }

      btn.addEventListener('click', activateSlide);
      btn.addEventListener('mouseenter', activateSlide);
    });

    // Global Enquiry Modal trigger delegation
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.product-enquiry-trigger-btn, [data-enquiry-btn], #OpenProductEnquiryBtn');
      if (trigger) {
        e.preventDefault();
        var modal = document.getElementById('ProductEnquiryModal');
        if (modal) {
          modal.style.display = 'flex';
          document.body.style.overflow = 'hidden';
        } else if (window.openProductEnquiryModal) {
          window.openProductEnquiryModal();
        }
      }
    });
  }

  // DOM Content Loaded Initializer
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHomepageEngine);
  } else {
    initHomepageEngine();
  }
})();
